// =====================================================================================
// FILE: src/components/ui/AssetGallery.jsx
// Pop-up asset gallery for browsing, searching, copying IDs, and previewing content
// =====================================================================================
import React, { useState, useMemo, useRef, useEffect } from "react";
import { THEME, FONTS } from "../../constants/theme";

// =====================================================================================
// DROPBOX URL HELPERS
// =====================================================================================

/**
 * Check if URL is a Dropbox URL
 */
function isDropboxUrl(url) {
  return url && (url.includes("dropbox.com") || url.includes("dl.dropboxusercontent.com"));
}

/**
 * Analyze Dropbox URL to determine if it's embeddable
 * Returns: { embeddable: boolean, type: 'image'|'video'|'mov'|'folder', embedUrl: string|null }
 */
function analyzeDropboxUrl(url) {
  if (!url || !isDropboxUrl(url)) {
    return { embeddable: false, type: null, embedUrl: null };
  }
  
  // Check for file extension in URL
  const hasExtension = /\.(jpg|jpeg|png|gif|mp4|mov|webm)(\?|$)/i.test(url);
  
  // Folder URLs (no extension, or /fo/ path)
  if (!hasExtension || url.includes("/fo/") || url.includes("/folder/")) {
    return { embeddable: false, type: "folder", embedUrl: null };
  }
  
  // MOV files - need Dropbox's player, can't embed directly
  if (/\.mov(\?|$)/i.test(url)) {
    return { embeddable: false, type: "mov", embedUrl: null };
  }
  
  // Images (jpg, png, gif) and MP4 - can embed with raw=1
  if (/\.(jpg|jpeg|png|gif)(\?|$)/i.test(url)) {
    const embedUrl = convertToDropboxRaw(url);
    return { embeddable: true, type: "image", embedUrl };
  }
  
  if (/\.mp4(\?|$)/i.test(url)) {
    const embedUrl = convertToDropboxRaw(url);
    return { embeddable: true, type: "video", embedUrl };
  }
  
  return { embeddable: false, type: "unknown", embedUrl: null };
}

/**
 * Convert Dropbox share URL to raw/embeddable URL
 */
function convertToDropboxRaw(url) {
  if (!url) return null;
  
  // Already a raw URL
  if (url.includes("dl.dropboxusercontent.com")) {
    return url;
  }
  
  // Convert www.dropbox.com to dl.dropboxusercontent.com
  let embedUrl = url;
  embedUrl = embedUrl.replace("www.dropbox.com", "dl.dropboxusercontent.com");
  
  // Handle query params
  if (embedUrl.includes("?")) {
    // Remove dl=0 and add raw=1
    embedUrl = embedUrl.replace(/[?&]dl=\d/, "");
    embedUrl = embedUrl.replace(/[?&]raw=\d/, "");
    embedUrl += (embedUrl.includes("?") ? "&" : "?") + "raw=1";
  } else {
    embedUrl += "?raw=1";
  }
  
  return embedUrl;
}

// =====================================================================================
// PREVIEW MODAL COMPONENT
// =====================================================================================
function PreviewModal({ asset, onClose }) {
  if (!asset) return null;

  // Get preview URL - prioritize Asset Preview URL field for videos
  const getPreviewUrl = () => {
    // Helper to extract URL from various formats
    const extractUrl = (data) => {
      if (!data) return null;
      
      // Array format (Airtable attachments)
      if (Array.isArray(data)) {
        const first = data[0];
        if (!first) return null;
        // Airtable attachment object with thumbnails
        if (first.thumbnails?.large?.url) return first.thumbnails.large.url;
        if (first.thumbnails?.full?.url) return first.thumbnails.full.url;
        if (first.url) return first.url;
        if (typeof first === 'string') return first;
      }
      
      // String format (might be comma/newline separated, possibly markdown link)
      if (typeof data === 'string' && data.trim()) {
        // Check for markdown link format: [text](url)
        const markdownMatch = data.match(/\[([^\]]*)\]\(([^)]+)\)/);
        if (markdownMatch) {
          return markdownMatch[2]; // Return the URL part
        }
        const urls = data.split(/[,\n]/).map(u => u.trim()).filter(Boolean);
        return urls[0];
      }
      
      return null;
    };
    
    // Check if this is a video based on title
    const isVideo = /\.(mp4|mov|webm|m4v|avi)/i.test(asset.title || '');
    
    // 1. For videos, try Asset Preview URL first (Dropbox video link)
    if (isVideo && asset.previewUrl) {
      const preview = extractUrl(asset.previewUrl);
      if (preview) return preview;
    }
    
    // 2. Try thumb field (for images - Airtable CDN)
    const thumbResult = extractUrl(asset.thumb);
    if (thumbResult && !isVideo) return thumbResult;
    
    // 3. Try Asset Preview URL (fallback for all types)
    if (asset.previewUrl) {
      const preview = extractUrl(asset.previewUrl);
      if (preview) return preview;
    }
    
    // 4. Try url field (full resolution)
    const urlResult = extractUrl(asset.url);
    if (urlResult) return urlResult;
    
    // 5. Fall back to thumb even for videos (at least show something)
    if (thumbResult) return thumbResult;
    
    return null;
  };

  const previewUrl = getPreviewUrl();
  const assetId = extractAssetId(asset.title);
  
  // Analyze Dropbox URL for embedding capability
  const dropboxInfo = analyzeDropboxUrl(previewUrl);
  
  // Determine display URL - use embeddable Dropbox URL if available
  const displayUrl = dropboxInfo.embeddable 
    ? dropboxInfo.embedUrl 
    : (!isDropboxUrl(previewUrl) ? previewUrl : null);
  
  // Detect if it's a video
  const displayIsVideo = dropboxInfo.type === "video" || (
    displayUrl && (
      /\.(mp4|mov|webm|m4v|avi|m3u8)/i.test(displayUrl) ||
      /\.(mp4|mov|webm|m4v|avi|m3u8)/i.test(asset.title || '')
    )
  );
  
  // If no displayable content and not an embeddable Dropbox, don't show modal
  if (!displayUrl && !dropboxInfo.embeddable) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.85)",
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Modal Content */}
        <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            background: THEME.dark,
            borderRadius: 12,
            overflow: "hidden",
            maxWidth: "85vw",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
          }}
        >
          {/* Header */}
          <div style={{
            padding: "12px 16px",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: FONTS.sm,
                fontWeight: FONTS.semibold,
                color: THEME.primary,
                fontFamily: "monospace",
              }}>
                {assetId}
              </div>
              <div style={{
                fontSize: FONTS.xs,
                color: "rgba(255,255,255,0.5)",
                fontFamily: FONTS.family,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                {asset.title}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: "rgba(255,255,255,0.1)",
                border: "none",
                borderRadius: 6,
                width: 32,
                height: 32,
                cursor: "pointer",
                color: "white",
                fontSize: 18,
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>

          {/* Preview */}
          <div style={{
            padding: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#0a0a0a",
            minHeight: 350,
            minWidth: 500,
          }}>
            {displayUrl ? (
              displayIsVideo ? (
                <video
                  src={displayUrl}
                  controls={true}
                  autoPlay={true}
                  playsInline={true}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    minHeight: 280,
                    borderRadius: 4,
                    backgroundColor: "#000",
                  }}
                  onError={(e) => {
                    console.log('Video failed to load:', displayUrl);
                    // Show error message
                    e.target.style.display = 'none';
                    const fallback = e.target.parentNode.querySelector('.preview-fallback');
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : (
                <img
                  src={displayUrl}
                  alt={asset.title}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    borderRadius: 4,
                    objectFit: "contain",
                  }}
                  onError={(e) => {
                    console.log('Image failed to load:', displayUrl);
                    e.target.style.display = 'none';
                    const fallback = e.target.parentNode.querySelector('.preview-fallback');
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              )
            ) : null}
            {/* Fallback for no preview or failed load */}
            <div 
              className="preview-fallback"
              style={{
                display: displayUrl ? 'none' : 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                color: "rgba(255,255,255,0.3)",
                fontSize: FONTS.md,
                fontFamily: FONTS.family,
              }}>
              <span style={{ fontSize: 48 }}>🎬</span>
              <span>No preview available</span>
              {(asset.thumb || asset.url) && typeof (asset.thumb || asset.url) === 'string' && (
                <a 
                  href={(asset.thumb || asset.url).split(/[,\n]/)[0].trim()} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    color: THEME.primary,
                    fontSize: FONTS.sm,
                    marginTop: 8,
                  }}
                >
                  Try opening in new tab →
                </a>
              )}
            </div>
          </div>

          {/* Footer with info */}
          {asset.destination && (
            <div style={{
              padding: "10px 16px",
              borderTop: "1px solid rgba(255,255,255,0.1)",
              fontSize: FONTS.xs,
              color: "rgba(255,255,255,0.5)",
              fontFamily: FONTS.family,
            }}>
              Type: <span style={{ color: "rgba(255,255,255,0.8)" }}>{asset.destination}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Extract asset ID (CLUB_UNIQUEID) from title
function extractAssetId(title) {
  if (!title) return null;
  const parts = title.split("_");
  if (parts.length >= 2) {
    return `${parts[0]}_${parts[1]}`;
  }
  return title;
}

// Get asset type label from destination or title
function getAssetType(asset) {
  if (asset.destination) {
    // Shorten common destination names
    const dest = asset.destination.toLowerCase();
    if (dest.includes('led') && dest.includes('ribbon')) return 'LED Ribbon';
    if (dest.includes('led')) return 'LED';
    if (dest.includes('tvc')) return 'TVC';
    if (dest.includes('iptv')) return 'IPTV';
    if (dest.includes('gfx')) return 'GFX';
    if (dest.includes('video')) return 'Video';
    if (dest.includes('super')) return 'Superscreen';
    if (dest.includes('corner')) return 'Corner';
    if (dest.includes('interchange')) return 'Interchange';
    return asset.destination.slice(0, 15);
  }
  return 'Asset';
}

export default function AssetGallery({ isOpen, onClose, assetCache }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [previewAsset, setPreviewAsset] = useState(null);
  const [hoverAsset, setHoverAsset] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const searchRef = useRef(null);

  // Focus search on open
  useEffect(() => {
    if (isOpen && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
    // Reset search when closing
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  // Flatten all assets from cache into a single searchable list
  const allAssets = useMemo(() => {
    if (!assetCache) return [];
    
    const assets = [];
    
    // Handle both Map and Object structures
    if (assetCache instanceof Map) {
      assetCache.forEach((asset, id) => {
        if (asset && asset.title) {
          assets.push({ ...asset, id });
        }
      });
    } else if (typeof assetCache === 'object') {
      Object.values(assetCache).forEach(categoryAssets => {
        if (Array.isArray(categoryAssets)) {
          categoryAssets.forEach(asset => {
            if (asset && asset.title) {
              assets.push(asset);
            }
          });
        } else if (categoryAssets && categoryAssets.title) {
          assets.push(categoryAssets);
        }
      });
    }
    
    // Remove duplicates by title and filter out archived assets
    const seen = new Set();
    return assets.filter(asset => {
      if (asset.archived) return false; // ✅ Exclude archived assets
      if (seen.has(asset.title)) return false;
      seen.add(asset.title);
      return true;
    }).sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }, [assetCache]);

  // Filter assets by search - show first 20 if no search, all matches if searching
  const filteredAssets = useMemo(() => {
    if (!searchTerm.trim()) {
      // No search - show first 20 only (lazy load)
      return allAssets.slice(0, 20);
    }
    
    const term = searchTerm.toLowerCase();
    return allAssets.filter(asset => 
      asset.title?.toLowerCase().includes(term) ||
      extractAssetId(asset.title)?.toLowerCase().includes(term) ||
      asset.destination?.toLowerCase().includes(term)
    );
  }, [allAssets, searchTerm]);

  // Copy ID to clipboard
  const copyId = (e, asset) => {
    e.stopPropagation();
    const id = extractAssetId(asset.title);
    if (!id) return;
    
    navigator.clipboard.writeText(id).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      const textarea = document.createElement('textarea');
      textarea.value = id;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  // Get thumbnail - handle multi-image (take first), handle Airtable attachment format
  const getThumbnail = (asset) => {
    // Check thumb field
    if (asset.thumb) {
      // If it's an array (Airtable attachments format)
      if (Array.isArray(asset.thumb)) {
        const first = asset.thumb[0];
        if (first?.thumbnails?.large?.url) return first.thumbnails.large.url;
        if (first?.url) return first.url;
        if (typeof first === 'string') return first;
      }
      // If it's a string (might be comma/newline separated)
      if (typeof asset.thumb === 'string') {
        const thumbs = asset.thumb.split(/[,\n]/).map(u => u.trim()).filter(Boolean);
        return thumbs[0];
      }
    }
    
    // Fallback to url field
    if (asset.url) {
      if (Array.isArray(asset.url)) {
        const first = asset.url[0];
        if (first?.thumbnails?.large?.url) return first.thumbnails.large.url;
        if (first?.url) return first.url;
        if (typeof first === 'string') return first;
      }
      if (typeof asset.url === 'string') {
        const urls = asset.url.split(/[,\n]/).map(u => u.trim()).filter(Boolean);
        return urls[0];
      }
    }
    
    return null;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 9994,
        }}
      />
      
      {/* Gallery Panel - Bottom Left */}
      <div
        style={{
          position: "fixed",
          bottom: 24,
          left: 24,
          width: 400,
          height: 560,
          background: THEME.dark,
          borderRadius: 16,
          boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
          border: "1px solid rgba(255,255,255,0.1)",
          display: "flex",
          flexDirection: "column",
          zIndex: 9995,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "14px 18px",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "rgba(255,255,255,0.03)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>🖼️</span>
            <div>
              <div style={{
                color: "white",
                fontSize: FONTS.md,
                fontWeight: FONTS.semibold,
                fontFamily: FONTS.family,
              }}>
                Asset Gallery
              </div>
              <div style={{
                color: "rgba(255,255,255,0.5)",
                fontSize: FONTS.xs,
                fontFamily: FONTS.family,
              }}>
                {searchTerm ? `${filteredAssets.length} results` : `${allAssets.length} total • Showing first 20`}
              </div>
            </div>
          </div>
          
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.1)",
              border: "none",
              borderRadius: 8,
              width: 32,
              height: 32,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(255,255,255,0.7)",
              fontSize: 18,
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
          >
            ×
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: "rgba(255,255,255,0.08)",
            borderRadius: 8,
            padding: "10px 12px",
            border: "1px solid rgba(255,255,255,0.1)",
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              placeholder="Search to find more assets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                color: "white",
                fontSize: FONTS.sm,
                fontFamily: FONTS.family,
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                style={{
                  background: "none",
                  border: "none",
                  color: "rgba(255,255,255,0.5)",
                  cursor: "pointer",
                  padding: 0,
                  fontSize: 14,
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Asset List */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          padding: 8,
        }}>
          {filteredAssets.length === 0 ? (
            <div style={{
              textAlign: "center",
              padding: 40,
              color: "rgba(255,255,255,0.5)",
              fontSize: FONTS.sm,
              fontFamily: FONTS.family,
            }}>
              {searchTerm ? "No assets match your search" : "No assets available"}
            </div>
          ) : (
            filteredAssets.map((asset, idx) => {
              const assetId = extractAssetId(asset.title);
              const isCopied = copiedId === assetId;
              const assetType = getAssetType(asset);
              const thumbnail = getThumbnail(asset);
              
              // Check if thumbnail is embeddable (Airtable CDN or embeddable Dropbox)
              const thumbDropboxInfo = analyzeDropboxUrl(thumbnail);
              const canPreview = thumbnail && (!isDropboxUrl(thumbnail) || thumbDropboxInfo.embeddable);
              
              return (
                <div
                  key={asset.title + idx}
                  onClick={() => {
                    // Open preview modal for embeddable content
                    if (canPreview) {
                      setPreviewAsset(asset);
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = THEME.primary;
                    e.currentTarget.style.background = "rgba(35,201,182,0.08)";
                    // Show hover preview
                    if (thumbnail) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setHoverAsset(asset);
                      setHoverPosition({ 
                        x: rect.right + 10, 
                        y: rect.top 
                      });
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)";
                    e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    setHoverAsset(null);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: 10,
                    marginBottom: 6,
                    background: "rgba(255,255,255,0.04)",
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.06)",
                    cursor: canPreview ? "pointer" : "default",
                    transition: "all 0.15s ease",
                  }}
                >
                  {/* Thumbnail */}
                  <div style={{
                    width: 56,
                    height: 40,
                    borderRadius: 6,
                    background: "#1a1a1a",
                    flexShrink: 0,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: 16, opacity: 0.3 }}>🎬</span>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* ID */}
                    <div style={{
                      fontSize: FONTS.sm,
                      fontWeight: FONTS.semibold,
                      color: THEME.primary,
                      fontFamily: "monospace",
                      marginBottom: 2,
                    }}>
                      {assetId}
                    </div>
                    {/* Type badge + name preview */}
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}>
                      <span style={{
                        fontSize: 10,
                        padding: "2px 6px",
                        background: "rgba(255,255,255,0.1)",
                        borderRadius: 4,
                        color: "rgba(255,255,255,0.7)",
                        fontFamily: FONTS.family,
                        whiteSpace: "nowrap",
                      }}>
                        {assetType}
                      </span>
                      <span style={{
                        fontSize: FONTS.xs,
                        color: "rgba(255,255,255,0.4)",
                        fontFamily: FONTS.family,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>
                        {asset.title?.split('_').slice(2).join('_') || ''}
                      </span>
                    </div>
                  </div>

                  {/* Copy Button */}
                  <button
                    onClick={(e) => copyId(e, asset)}
                    style={{
                      padding: "6px 12px",
                      background: isCopied ? THEME.primary : "rgba(255,255,255,0.1)",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      color: isCopied ? THEME.textOnPrimary : "rgba(255,255,255,0.7)",
                      fontSize: FONTS.xs,
                      fontFamily: FONTS.family,
                      fontWeight: FONTS.medium,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      transition: "all 0.15s ease",
                      flexShrink: 0,
                      minWidth: isCopied ? 70 : "auto",
                    }}
                    onMouseEnter={(e) => {
                      if (!isCopied) e.currentTarget.style.background = "rgba(255,255,255,0.15)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isCopied) e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                    }}
                  >
                    {isCopied ? "✓ Copied!" : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    )}
                  </button>
                </div>
              );
            })
          )}
          
          {/* Show hint if showing limited results */}
          {!searchTerm && allAssets.length > 20 && (
            <div style={{
              textAlign: "center",
              padding: "16px 12px",
              color: "rgba(255,255,255,0.4)",
              fontSize: FONTS.xs,
              fontFamily: FONTS.family,
              borderTop: "1px solid rgba(255,255,255,0.06)",
              marginTop: 8,
            }}>
              Search to find more from {allAssets.length} assets
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div style={{
          padding: "10px 16px",
          borderTop: "1px solid rgba(255,255,255,0.1)",
          background: "rgba(255,255,255,0.03)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
        }}>
          <span style={{
            fontSize: FONTS.xs,
            color: "rgba(255,255,255,0.4)",
            fontFamily: FONTS.family,
          }}>
            Hover to preview • Click icon to copy ID
          </span>
        </div>
      </div>

      {/* Hover Preview Tooltip */}
      {hoverAsset && getThumbnail(hoverAsset) && (() => {
        const hoverThumb = getThumbnail(hoverAsset);
        const hoverDropboxInfo = analyzeDropboxUrl(hoverThumb);
        const hoverDisplayUrl = hoverDropboxInfo.embeddable 
          ? hoverDropboxInfo.embedUrl 
          : (!isDropboxUrl(hoverThumb) ? hoverThumb : null);
        
        if (!hoverDisplayUrl) return null;
        
        return (
          <div
            style={{
              position: "fixed",
              left: Math.min(hoverPosition.x, window.innerWidth - 320),
              top: Math.max(10, Math.min(hoverPosition.y, window.innerHeight - 220)),
              width: 300,
              background: THEME.dark,
              borderRadius: 10,
              boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              border: "1px solid rgba(255,255,255,0.15)",
              overflow: "hidden",
              zIndex: 10001,
              pointerEvents: "none",
            }}
          >
            {/* Large Preview Image */}
            <div style={{
              width: "100%",
              height: 180,
              background: "#0a0a0a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <img
                src={hoverDisplayUrl}
                alt=""
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </div>
            {/* Asset Info */}
            <div style={{
              padding: "10px 12px",
              borderTop: "1px solid rgba(255,255,255,0.1)",
            }}>
              <div style={{
                fontSize: FONTS.sm,
                fontWeight: FONTS.semibold,
                color: THEME.primary,
                fontFamily: "monospace",
                marginBottom: 4,
              }}>
                {extractAssetId(hoverAsset.title)}
              </div>
              <div style={{
                fontSize: FONTS.xs,
                color: "rgba(255,255,255,0.5)",
                fontFamily: FONTS.family,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                {hoverAsset.destination || 'Asset'}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Preview Modal - for non-Dropbox content */}
      {previewAsset && (
        <PreviewModal
          asset={previewAsset}
          onClose={() => setPreviewAsset(null)}
        />
      )}
    </>
  );
}
